// Course types — what a Course actually is and how the runner
// understands its steps.
//
// A Course is a *sequence* of steps that the runner walks through
// linearly. Three step kinds, each with a single responsibility:
//
//   - `experiment` — overlay an `ExperimentSpec` onto the current
//     world. The runner calls `sandbox.runExperiment(spec)`; since
//     the engine doesn't reset state between calls, a spec with
//     `clear` resets the grid (use this for the setup step), and
//     a spec with just a `heat` or `paint` action layers that
//     action over whatever's already on the canvas (use this for
//     mid-course interactions).
//
//   - `wait` — let the world tick for N simulation steps before
//     advancing. The runner unpauses the sandbox, polls
//     `tickCount()`, and re-pauses once the tick budget is spent.
//
//   - `narrate` — pause the world, show an Axie speech bubble with
//     `says` text and an optional pose, and wait for the user to
//     click Continue. Only step kind that requires user input.
//
// This shape is intentionally minimal: every multi-stage tutorial
// in the courses library composes from these three primitives.
// See `data.ts` for the eight bundled courses and
// `useCourseRunner.ts` for the state machine.

import type { MascotAnimation } from "./mascot-animation";

/** Top-level difficulty band; one chapter of the course book. */
export type CourseMode = "starter" | "explorer" | "inventor" | "genius";

export const MODES: CourseMode[] = ["starter", "explorer", "inventor", "genius"];

export const MODE_META: Record<
  CourseMode,
  { title: string; tagline: string; badge: string }
> = {
  starter: {
    title: "Starter",
    tagline: "First steps in the sandbox.",
    badge: "★",
  },
  explorer: {
    title: "Explorer",
    tagline: "Discover how materials react.",
    badge: "★★",
  },
  inventor: {
    title: "Inventor",
    tagline: "Build something with what you've learned.",
    badge: "★★★",
  },
  genius: {
    title: "Genius",
    tagline: "Real chemistry, real physics.",
    badge: "★★★★",
  },
};

/** Subset of the experiment-JSON contract from `EXPERIMENTS.md` the
 *  course runner actually constructs. Mirrors the engine's
 *  `ExperimentSpec` (see `crates/sim-core/src/experiment.rs`). */
export interface ExperimentSpec {
  name: string;
  description?: string;
  grid: { width: number; height: number };
  seed?: number;
  actions?: Array<{ tick: number; kind: string; params: unknown }>;
  expectations?: Array<{ tick: number; kind: string; params: unknown }>;
  schema_version?: number;
}

/** Step types — see file header for semantics. */
export type CourseStep =
  | {
      kind: "narrate";
      /** Axie's line. Plain text; rendered in a speech bubble. */
      says: string;
      /** Optional pose hint passed to the mascot renderer. */
      animation?: MascotAnimation;
    }
  | { kind: "experiment"; spec: ExperimentSpec }
  | { kind: "wait"; ticks: number };

export interface Course {
  /** Stable id used by the URL fragment and analytics; snake_case. */
  id: string;
  mode: CourseMode;
  /** Short display name. */
  title: string;
  /** One-line summary shown on the mode page card. */
  summary: string;
  /** Walked top-to-bottom by the runner. */
  steps: CourseStep[];
}
